import {useRef} from 'react';
import {useQuery} from 'react-query';
import {useLocation} from 'react-router-dom';
import {paymentAPI} from 'api';
import {getTransactionStatus, getLASGCharges, validateLASGReference} from 'api/routes/wallet';
import {getVersion} from 'utils/helpers';

const MAX_POLL_MS = 120000; // 2 minutes
const POLL_INTERVAL_MS = 5000;

export const useLASGReceipt = checkoutRef => {
    const startTime = useRef(Date.now());
    const location = useLocation();
    const {API, apiVersion} = getVersion(location);

    const query = useQuery(
        ['lasg-receipt', checkoutRef],
        async () => {
            const response = await API.get(`${getTransactionStatus(checkoutRef, apiVersion)}`);
            return response.data;
        },
        {
            enabled: !!checkoutRef,
            retry: false,
            refetchOnWindowFocus: false,
            refetchInterval: data => {
                const hasReceipt = !!data?.data?.transaction?.metadata?.lasg?.receipt_url;
                const timedOut = Date.now() - startTime.current > MAX_POLL_MS;
                return hasReceipt || timedOut ? false : POLL_INTERVAL_MS;
            },
        }
    );

    const receipt = query.data?.data?.transaction?.metadata?.lasg;
    const hasReceipt = !!receipt?.receipt_url;
    const timedOut = !hasReceipt && Date.now() - startTime.current > MAX_POLL_MS;
    return {...query, receipt, hasReceipt, timedOut};
};

export const useLASGValidate = (reference, type, {onSuccess} = {}) => {
    const query = useQuery(
        ['validate-lasg', reference, type],
        async () => {
            const response = await paymentAPI.get(validateLASGReference, {
                params: {reference, type},
            });
            if (!response.data?.success) {
                throw new Error(response.data?.message || 'LASG validation failed.');
            }
            return response.data;
        },
        {
            enabled: !!reference && !!type,
            retry: false,
            refetchOnWindowFocus: false,
            keepPreviousData: false,
            onSuccess,
        }
    );

    const invoiceData = query.data?.data;

    return {...query, invoiceData};
};

export const useLASGCharges = ({amount, currency = 'NGN', publicKey}) => {
    const validAmount = Number(amount) >= 50;

    const query = useQuery(
        ['lasg-charges', amount, currency],
        async () => {
            const response = await paymentAPI.get(getLASGCharges, {
                params: {amount, currency},
                headers: {Authorization: publicKey},
            });
            return response.data;
        },
        {
            enabled: validAmount,
            retry: false,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
        }
    );

    const chargesData = query.data?.data;
    return {...query, chargesData};
};
